import React, { memo, useEffect } from 'react';

import { HighlightType } from '../../types/HighlightType';
import { TextColor } from '../../types/TextColor';
import { TextVariant } from '../../types/TextVariant';
import { Box } from '../Box/Box';
import { useShowMoreLines } from '../hooks/useShowMoreLines';
import { useShowMoreText } from '../hooks/useShowMoreText';
import { Layout } from '../Layout/Layout';
import { MarkedText } from '../MarkedText/MarkedText';
import { Text } from '../Text/Text';

interface Props {
  /** Source text */
  text: string;
  /** Text color */
  color?: TextColor;
  /** Full content to render if all text is shown */
  fullContent?: React.ReactNode;
  /** Specifies the highlighter for text matching on the `search` string */
  highlighter?: HighlightType;
  /** Indicates if the text should be expanded */
  isExpanded?: boolean;
  /** Limit the number of visible characters */
  limit?: number;
  /**
   * Limit the number of visible rendered lines.
   * Activates line-based mode and takes precedence over `limit` when set.
   * Truncation responds automatically to container width changes via ResizeObserver.
   */
  limitLines?: number;
  /** Indicates if Show More / Show Less should display as text or an icon button */
  mode?: 'text' | 'icon';
  /** Enables text matching based on the search string */
  search?: string;
  /** Number of hidden characters to display (char mode only) */
  step?: number;
  /** Number of additional lines to reveal per Show More click (line mode only; defaults to limitLines) */
  stepLines?: number;
  /** When set to true, it will reset to its initial state (closed) */
  // TODO: this prop smells and we should analyze it at some point
  shouldResetToInitialState?: boolean;
  /** Text variant */
  variant?: TextVariant;
  whiteSpace?: 'pre-wrap' | 'normal';
}

interface LinesModeProps extends Omit<Props, 'limitLines'> {
  limitLines: number;
}

const CollapsibleTextByLines = ({
  color,
  fullContent,
  highlighter,
  isExpanded = false,
  limitLines,
  mode = 'text',
  search,
  shouldResetToInitialState,
  stepLines,
  text,
  variant,
  whiteSpace,
}: LinesModeProps) => {
  const {
    clampStyle,
    handleShowLess,
    handleShowMore,
    shouldShowMore,
    showMore,
    textRef,
  } = useShowMoreLines({ limitLines, mode, stepLines });

  useEffect(() => {
    if (isExpanded) handleShowMore();
  }, [isExpanded, handleShowMore]);

  useEffect(() => {
    if (shouldResetToInitialState) handleShowLess();
  }, [shouldResetToInitialState, handleShowLess]);

  const content =
    !shouldShowMore && fullContent ? (
      fullContent
    ) : (
      <Text variant={variant} color={color}>
        <MarkedText
          highlighter={highlighter}
          search={search}
          text={text}
          whiteSpace={whiteSpace}
        />
      </Text>
    );

  if (mode === 'icon') {
    return (
      <Layout direction="row" justify="space-between">
        <Box minW="0">
          <div style={clampStyle}>
            <div ref={textRef}>{content}</div>
          </div>
        </Box>
        {showMore}
      </Layout>
    );
  }

  return (
    <Layout direction="column" align="flex-start" spacing={1}>
      <div style={clampStyle}>
        <div ref={textRef}>{content}</div>
      </div>
      {showMore && <Box>{showMore}</Box>}
    </Layout>
  );
};

const CollapsibleTextByChars = ({
  color,
  fullContent,
  highlighter,
  isExpanded = false,
  limit,
  mode = 'text',
  search,
  shouldResetToInitialState,
  step,
  text,
  variant,
  whiteSpace,
}: Props) => {
  const resolvedLimit = limit ?? text.length;

  const {
    visibleText,
    showMore,
    shouldShowMore,
    handleShowLess,
    handleShowMore,
  } = useShowMoreText({
    limit: resolvedLimit,
    mode,
    step,
    text,
  });

  useEffect(() => {
    if (isExpanded) handleShowMore();
  }, [isExpanded, handleShowMore]);

  useEffect(() => {
    if (shouldResetToInitialState) handleShowLess();
  }, [shouldResetToInitialState, handleShowLess]);

  const visibleContent =
    !shouldShowMore && fullContent ? (
      fullContent
    ) : (
      <Text variant={variant} color={color}>
        <MarkedText
          highlighter={highlighter}
          search={search}
          text={visibleText}
          whiteSpace={whiteSpace}
        />
      </Text>
    );

  return mode === 'icon' ? (
    <Layout direction="row" justify="space-between">
      <Box minW="0">{visibleContent}</Box>
      {showMore}
    </Layout>
  ) : (
    <span>
      {visibleContent} {showMore}
    </span>
  );
};

export const CollapsibleText = memo(({ limitLines, ...rest }: Props) =>
  limitLines !== undefined ? (
    <CollapsibleTextByLines {...rest} limitLines={limitLines} />
  ) : (
    <CollapsibleTextByChars {...rest} />
  ),
);
